// src/components/SessionTimer.tsx

import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface SessionTimerProps {
    timeRemaining: number;
}

export const SessionTimer: React.FC<SessionTimerProps> = ({ timeRemaining }) => {
    const [remainingTime, setRemainingTime] = useState(timeRemaining);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Reset the timer whenever the initial time prop changes
    useEffect(() => {
        setRemainingTime(timeRemaining);
    }, [timeRemaining]);

    // Countdown logic
    useEffect(() => {
        if (remainingTime <= 0) return;

        const interval = setInterval(() => {
            setRemainingTime((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [remainingTime]);

    const handleRefreshSession = async () => {
        setIsRefreshing(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken: localStorage.getItem('refreshToken') }),
            });

            const data = await response.json();

            if (data.success && data.data.token) {
                localStorage.setItem('token', data.data.token);
                setRemainingTime(data.data.remainingTime);
            } else {
                // Consider a more robust error handling, e.g., using a snackbar
                alert('Session could not be refreshed. You may need to log in again.');
            }
        } catch (error) {
            console.error('Failed to refresh session:', error);
            alert('An error occurred while refreshing the session.');
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: 13,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isRefreshing && <CircularProgress size={14} color="inherit" />}
                <Typography
                    variant="caption"
                    onClick={handleRefreshSession}
                    sx={{
                        cursor: isRefreshing ? 'default' : 'pointer',
                        '&:hover': {
                            textDecoration: isRefreshing ? 'none' : 'underline',
                        },
                    }}
                >
                    Time Remaining:
                </Typography>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                <SecondsToHumanReadable seconds={remainingTime} />
            </Typography>
        </Box>
    );
};
// src/components/SecondsToHumanReadable.tsx

interface SecondsToHumanReadableProps {
    seconds: number;
}

export const SecondsToHumanReadable: React.FC<SecondsToHumanReadableProps> = ({ seconds }) => {
    // Ensure the input is a non-negative number
    const safeSeconds = Math.max(0, seconds);

    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = safeSeconds % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');

    return (
        <span>
            {pad(hours)}:{pad(minutes)}:{pad(remainingSeconds)}
        </span>
    );
};